import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FaSave, FaPlus, FaUserCheck, FaSearch } from "react-icons/fa";
import { Button } from 'components/common';
import { getPurchaseSignerSites, getPurchaseSignerSettings, updatePurchaseSignerSettings, searchPurchaseSignerUsers } from "../common/settingsApi";
import UserPicker from "../common/UserPicker";

// Admin: chọn Financial Controller / GM cho TỪNG SITE (HO, VTF, VTF1, ...).
// Dùng chung bởi cả Purchase Requisition (Head of Dept do requester chọn tay, không ở đây)
// và Purchase Order ("Checked by"/Finance Dept = Financial Controller, "Approved by" = GM).
//
// UX (giống NotificationRulesPanel của Payment Request): chỉ hiển thị các site ĐÃ cấu hình
// (dạng hàng). Nút "Add site" cho phép thêm site mới từ danh sách site còn lại — mở rộng
// dần khi tổ chức có thêm site. Toolbar (Add site + Save) luôn cố định phía trên, không
// phải cuộn xuống cuối mới thấy.
const ROLES = [
  { role_type: "financial_controller", label: "Financial Controller" },
  { role_type: "gm", label: "GM / General Manager" },
];
const emptySigners = () => Object.fromEntries(ROLES.map((r) => [r.role_type, null]));

const SignersPanel = () => {
  const [sites, setSites] = useState([]);           // tất cả site: [{ value, label }]
  const [map, setMap] = useState({});                // site_id -> { [role_type]: {id,email,name}|null }
  const [rows, setRows] = useState([]);              // site_id[] — các site đang hiển thị
  const [originalIds, setOriginalIds] = useState([]); // site_id[] có cấu hình lúc load (để clear khi bỏ)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);       // mở dropdown "Add site"
  const [siteQ, setSiteQ] = useState("");            // filter trong dropdown Add site
  const addRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [siteRes, cfgRes] = await Promise.all([getPurchaseSignerSites(), getPurchaseSignerSettings()]);
      setSites(siteRes?.data || []);
      const m = {};
      const ids = [];
      (cfgRes?.data || []).forEach((r) => {
        m[r.site_id] = {};
        ROLES.forEach((role) => {
          const s = (r.signers || []).find((x) => x.role_type === role.role_type);
          m[r.site_id][role.role_type] = s?.user_id ? { id: s.user_id, email: s.email, name: s.name } : null;
        });
        ids.push(r.site_id);
      });
      setMap(m);
      setRows(ids);
      setOriginalIds(ids);
    } catch {
      toast.error("Failed to load signer settings.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Đóng dropdown Add site khi click ngoài.
  useEffect(() => {
    if (!adding) return undefined;
    const h = (e) => { if (!addRef.current?.contains(e.target)) setAdding(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [adding]);

  const siteLabel = (id) => sites.find((s) => String(s.value) === String(id))?.label || `Site #${id}`;

  const setSigner = (siteId, roleType, user) => {
    setMap((prev) => ({ ...prev, [siteId]: { ...(prev[siteId] || emptySigners()), [roleType]: user } }));
  };

  const countOf = (id) => ROLES.filter((r) => map[id]?.[r.role_type]?.id).length;

  const availableSites = useMemo(() => {
    const term = siteQ.trim().toLowerCase();
    return sites
      .filter((s) => !rows.includes(s.value))
      .filter((s) => !term || String(s.label).toLowerCase().includes(term));
  }, [sites, rows, siteQ]);

  const addSite = (siteId) => {
    setRows((r) => (r.includes(siteId) ? r : [...r, siteId]));
    setMap((m) => ({ ...m, [siteId]: m[siteId] || emptySigners() }));
    setAdding(false);
    setSiteQ("");
  };

  const removeRow = (siteId) => {
    setRows((r) => r.filter((id) => id !== siteId));
    setMap((m) => { const next = { ...m }; delete next[siteId]; return next; });
  };

  const assignedCount = useMemo(
    () => rows.filter((id) => ROLES.some((r) => map[id]?.[r.role_type]?.id)).length,
    [rows, map]
  );

  const save = async () => {
    setSaving(true);
    try {
      const removed = originalIds.filter((id) => !rows.includes(id));
      const settings = [
        ...rows.flatMap((id) => ROLES.map((r) => ({ site_id: id, role_type: r.role_type, user_id: map[id]?.[r.role_type]?.id || "" }))),
        ...removed.flatMap((id) => ROLES.map((r) => ({ site_id: id, role_type: r.role_type, user_id: "" }))),
      ];
      await updatePurchaseSignerSettings(settings);
      toast.success("Signer settings saved.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to save signer settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-min-h-0">
      {/* Toolbar cố định phía trên — Add site + Save luôn thấy, không phải cuộn xuống cuối. */}
      <div className="tw-flex tw-items-start tw-gap-3 tw-mb-4 tw-flex-shrink-0">
        <span className="tw-inline-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-geist-md tw-bg-geist-100 tw-text-geist-accent">
          <FaUserCheck size={14} />
        </span>
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-sm tw-font-semibold tw-text-geist-fg">Signer settings</span>
            <span className="tw-text-[11px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2">
              {assignedCount} configured
            </span>
          </div>
          <p className="tw-text-xs tw-text-geist-fg-3 tw-m-0 tw-mt-0.5 tw-max-w-lg">
            Choose the Financial Controller and GM who sign Purchase Requisitions / Purchase Orders for each site.
            Head of Dept is chosen by the requester on each requisition, not configured here.
          </p>
        </div>

        {/* Add site — dropdown chọn site còn lại */}
        <div ref={addRef} className="tw-relative tw-flex-shrink-0">
          <Button
            variant="secondary" size="sm" leftIcon={FaPlus}
            disabled={loading || saving}
            onClick={() => setAdding((v) => !v)}
          >
            Add site
          </Button>
          {adding && (
            <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-w-64 tw-z-50 tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-overflow-hidden">
              <div className="tw-p-2 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
                <div className="tw-flex tw-items-center tw-gap-2 tw-bg-geist-bg-2 tw-rounded-geist-sm tw-px-2.5 tw-py-1.5">
                  <FaSearch size={11} className="tw-text-geist-fg-3 tw-flex-shrink-0" />
                  <input
                    autoFocus value={siteQ} onChange={(e) => setSiteQ(e.target.value)}
                    placeholder="Search sites…"
                    className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3"
                  />
                </div>
              </div>
              <div className="tw-max-h-60 tw-overflow-y-auto tw-py-1">
                {availableSites.length === 0 ? (
                  <div className="tw-px-3 tw-py-4 tw-text-xs tw-text-geist-fg-3 tw-text-center">
                    {sites.length === rows.length ? "All sites added." : "No sites match."}
                  </div>
                ) : (
                  availableSites.map((s) => (
                    <div
                      key={s.value} role="button" tabIndex={0}
                      onClick={() => addSite(s.value)}
                      className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-cursor-pointer hover:tw-bg-geist-100 tw-transition-colors"
                    >
                      <FaPlus size={9} className="tw-text-geist-fg-3 tw-flex-shrink-0" />
                      <span className="tw-text-[13px] tw-text-geist-fg tw-truncate">{s.label}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <Button
          variant="primary" size="sm" leftIcon={FaSave}
          disabled={saving || loading}
          onClick={save}
          className="tw-flex-shrink-0"
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      {/* List — chỉ các site đã thêm. */}
      {loading ? (
        <div className="tw-py-12 tw-text-center tw-text-geist-fg-3 tw-text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="tw-rounded-geist-md tw-border tw-border-dashed tw-border-geist-border-2 tw-py-12 tw-px-4 tw-text-center">
          <div className="tw-text-sm tw-text-geist-fg-2 tw-font-medium">No sites configured yet</div>
          <div className="tw-text-xs tw-text-geist-fg-3 tw-mt-1">
            Click <span className="tw-font-semibold">Add site</span> to set signers for a site.
          </div>
        </div>
      ) : (
        <div className="tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-bg-white tw-divide-y tw-divide-geist-border">
          {rows.map((id) => {
            const cur = map[id] || emptySigners();
            const total = countOf(id);
            return (
              <div key={id} className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-start tw-gap-2 sm:tw-gap-4 tw-px-4 tw-py-3">
                <div className="sm:tw-w-40 tw-flex-shrink-0 tw-flex tw-items-center tw-gap-2 tw-min-w-0 sm:tw-pt-2">
                  <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-xl tw-flex-shrink-0 ${total === ROLES.length ? "tw-bg-geist-success" : total ? "tw-bg-geist-warning" : "tw-bg-geist-border-2"}`} />
                  <span className="tw-text-[13px] tw-font-medium tw-text-geist-fg tw-truncate">{siteLabel(id)}</span>
                </div>
                <div className="tw-flex-1 tw-min-w-0 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                  {ROLES.map((r) => (
                    <div key={r.role_type}>
                      <div className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-geist-fg-3 tw-mb-1">{r.label}</div>
                      <UserPicker
                        value={cur[r.role_type]}
                        onChange={(u) => setSigner(id, r.role_type, u)}
                        fetcher={searchPurchaseSignerUsers}
                        placeholder={`Select ${r.label}...`}
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button" title="Remove site"
                  onClick={() => removeRow(id)}
                  className="tw-flex-shrink-0 tw-mt-1 tw-h-7 tw-w-7 tw-inline-flex tw-items-center tw-justify-center tw-rounded-geist-sm tw-border-0 tw-bg-transparent tw-text-geist-fg-3 hover:tw-bg-geist-100 hover:tw-text-geist-error tw-transition-colors"
                >
                  <i className="bi bi-trash tw-text-[13px]" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SignersPanel;
